import { GridColumnSettings, GridColumnType, GridTemplateCondition } from '@/shared/types/grid';
import { gridTemplateCondition } from '@/shared/utils/grid';
import { classes } from '@/shared/utils/style';

export type GridButtonAction = 'setDateToToday';

export type GridButtonProps<T extends { id: string }> = {
  id: string; // Primary key
  uid?: string;
  field: keyof T & string;
  type: GridColumnType;
  action: GridButtonAction;
  toggled?: string;
};

export const buttonColumn = <T extends { id: string }>(
  options: Omit<GridButtonProps<T>, 'id' | 'toggle'> & {
    header: string;
    text?: string;
    class?: string;
    iconClass?: string;
    toggleIf?: GridTemplateCondition<T>;
    hideIf?: GridTemplateCondition<T>;
    visible?: boolean;
  },
): GridColumnSettings<T> => {
  const { uid, field, header, type, action, text, class: cls, iconClass, toggleIf, hideIf, visible } = options ?? {};

  const toggleCondition = toggleIf ? gridTemplateCondition(toggleIf) : undefined;

  const hideCondition = hideIf ? gridTemplateCondition(hideIf) : undefined;

  const template = `
  <button class="${classes(
    'button',
    'grid-button',
    'btn',
    'btn-sm',
    cls ?? 'btn-outline-primary',
  )}" data-id="\${id}" data-field="${field}" data-action="${action}" ${
    toggleIf ? `data-toggled="\${if(${toggleCondition})}true\${else}false\${/if}"` : ''
  } ${hideIf ? `data-hide="\${if(${hideCondition})}true\${else}false\${/if}"` : ''}>
      ${iconClass ? `<i class="${classes(iconClass, text && ' me-2')}"></i>` : ''}
      ${text ? `<span>${text}</span>` : ''} 
  </button>`;

  return {
    uid: uid ?? field,
    // Always set to id as we don't need to sort and filter button columns and if we set to a column we do
    // want to be able to sort and filter on then if the button column appears before the filter column it
    // will prevent the filter from working (seems EJ2 grid only allows one column to filter a given field)
    field: 'id',
    headerText: header,
    headerTemplate: iconClass
      ? `<i class="grid-tooltip ${iconClass} text-more-muted" title="${header}" data-bs-placement="top"></i>`
      : undefined,
    type,
    template,
    headerTextAlign: 'Center',
    textAlign: 'Center',
    allowSorting: false,
    allowFiltering: false,
    width: 50,
    visible: visible ?? true,
  };
};
